
/**
 * Environment detection utility for database switching
 * Development environments: localhost, 127.0.0.1, .lovable.app previews, dev.wellsuites.co
 * Production environments: .netlify.app deployments and all other domains
 */

// ============================================================
// ENVIRONMENT DETECTION TOGGLE
// ============================================================
// Set to false to disable environment detection and force production database
// Set to true to enable automatic environment detection
const ENVIRONMENT_DETECTION_ENABLED = false;
// ============================================================

interface SupabaseConfig {
  url: string;
  anonKey: string;
}

const DEVELOPMENT_SUPABASE_CONFIG: SupabaseConfig = {
  url: "https://yhugzzpueepzyyluritu.supabase.co", // Development database
  anonKey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlodWd6enB1ZWVwenl5bHVyaXR1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTAzNTcwMTgsImV4cCI6MjA2NTkzMzAxOH0.4BawB4emNsJurMnuxwFOcFFom_N8fn4Tmi_ukylrNEQ"
};

const PRODUCTION_SUPABASE_CONFIG: SupabaseConfig = {
  url: "https://doyqmjwlizoqfuabvluf.supabase.co", // Production database
  anonKey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRveXFtandsaXpvcWZ1YWJ2bHVmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDgxNzA5ODYsImV4cCI6MjA2Mzc0Njk4Nn0.vdKfRglNWF4YRhGLGHG3R6LGePLJE9FUpkaL1lS_dg8"
};

/**
 * Determines if the current environment should use the development database
 * @param origin - The origin URL to check (defaults to window.location.origin in browser)
 * @returns true if this is a development environment
 */
export const isTestEnvironment = (origin?: string): boolean => {
  // If environment detection is disabled, always return false (use production)
  if (!ENVIRONMENT_DETECTION_ENABLED) {
    return false;
  }

  // For server-side environments, use the provided origin
  if (origin) {
    return origin.includes("preview--") || 
           origin.includes("localhost") || 
           origin.includes("127.0.0.1") ||
           origin.includes(".lovable.app") ||
           origin.includes("dev.wellsuites.co");
  }
  
  // For client-side environments, check the current origin
  if (typeof window !== 'undefined') {
    const currentOrigin = window.location.origin;
    return currentOrigin.includes("preview--") || 
           currentOrigin.includes("localhost") || 
           currentOrigin.includes("127.0.0.1") ||
           currentOrigin.includes(".lovable.app") ||
           currentOrigin.includes("dev.wellsuites.co");
  }
  
  // Default to production if we can't determine the environment
  return false;
};

/**
 * Gets the appropriate Supabase configuration based on the environment
 * @param origin - Optional origin URL for server-side environments
 * @returns Supabase configuration object
 */
export const getSupabaseConfig = (origin?: string): SupabaseConfig => {
  const isDev = isTestEnvironment(origin);
  
  console.log('Environment detection:', {
    origin: origin || (typeof window !== 'undefined' ? window.location.origin : 'server'),
    environmentDetectionEnabled: ENVIRONMENT_DETECTION_ENABLED,
    isDevelopment: isDev,
    configType: isDev ? 'DEVELOPMENT' : 'PRODUCTION',
    forcedMode: !ENVIRONMENT_DETECTION_ENABLED ? 'FORCED_PRODUCTION' : 'AUTO_DETECT'
  });
  
  return isDev ? DEVELOPMENT_SUPABASE_CONFIG : PRODUCTION_SUPABASE_CONFIG;
};

/**
 * Gets the current environment name for logging purposes
 * @param origin - Optional origin URL for server-side environments
 * @returns Environment name string
 */
export const getEnvironmentName = (origin?: string): string => {
  // If environment detection is disabled, always return production
  if (!ENVIRONMENT_DETECTION_ENABLED) {
    return "forced-production";
  }

  const testOrigin = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  
  if (testOrigin.includes("localhost") || testOrigin.includes("127.0.0.1")) {
    return "local";
  } else if (testOrigin.includes("preview--")) {
    return "lovable-preview";
  } else if (testOrigin.includes(".lovable.app")) {
    return "lovable-app";
  } else if (testOrigin.includes("dev.wellsuites.co")) {
    return "dev-wellsuites";
  } else if (testOrigin.includes(".netlify.app")) {
    return "netlify-production";
  } else {
    return "production";
  }
};
